// Small app-level helpers that sit on top of the sim's public API — nothing
// here reaches into sim internals, and nothing in sim knows this exists.
import type { LevelDef, World } from '../sim/index.ts';
import { layoutLevel } from '../sim/index.ts';

/** 'paused' is app-only: the world stays 'playing' underneath, the loop just
 *  stops stepping it (src/app/loop.ts), so resuming is exact. */
export type AppState = 'intro' | 'playing' | 'paused' | 'dead' | 'cleared';

/**
 * Re-lay-out runways/pads on the *same* world after a resize/orientation
 * change (`layoutLevel` is the sim's pure layout function; this just wires
 * it into a live world and carries existing aircraft across proportionally,
 * since the sim itself never mutates a world's size on its own).
 */
export function relayout(world: World, newW: number, newH: number): void {
  const oldW = world.W, oldH = world.H;
  if (oldW === newW && oldH === newH) return;
  const sx = oldW > 0 ? newW / oldW : 1;
  const sy = oldH > 0 ? newH / oldH : 1;
  const layout = layoutLevel(world.level, newW, newH);
  const runwayById = new Map(layout.runways.map(r => [r.id, r]));
  const padById = new Map(layout.pads.map(p => [p.id, p]));

  for (const p of world.planes) {
    p.x *= sx; p.y *= sy;
    for (const pt of p.path) { pt.x *= sx; pt.y *= sy; }
    if (p.runway) p.runway = runwayById.get(p.runway.id) ?? null;
    if (p.pad) p.pad = padById.get(p.pad.id) ?? null;
  }

  world.W = newW; world.H = newH;
  world.layout = layout;
  world.runways = layout.runways;
  world.pads = layout.pads;
}

export function nextLevel(levels: readonly LevelDef[], currentId: string): LevelDef | null {
  const i = levels.findIndex(l => l.id === currentId);
  if (i < 0 || i + 1 >= levels.length) return null;
  return levels[i + 1];
}

/**
 * The one-line "how did this run compare" read on the game-over veil.
 * `prevBest` is the standing best *before* this run, so a run that merely
 * equals it is not announced as beating it; a first run (no prior best) gets
 * no line at all.
 */
export function runScoreLine(points: number, prevBest: number): string {
  if (points > prevBest) return 'a new best';
  if (prevBest > 0) return `best ${prevBest}`;
  return '';
}
